'use client';

import React from 'react';
import { Drawer } from 'vaul';
import { X } from 'lucide-react';
import { cn } from '../utils';
import { Popover, type PopoverProps } from './Popover';
import { useMediaQuery } from '../hooks/useMediaQuery';

export interface RichPopoverProps extends Omit<PopoverProps, 'title'> {
  /** Rendered as the drawer title bar on mobile. Not used on desktop — content is expected to carry its own header. */
  header: React.ReactNode;
  /** Optional sr-only description for the mobile Drawer. */
  drawerDescription?: string;
  /** Told when the popover or drawer opens and closes. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Dispatches to a positioned Popover on desktop (>=768px) or a Vaul bottom-sheet Drawer on mobile.
 * The `trigger` is rendered on both platforms; `children` is the body content.
 */
export function RichPopover({
  trigger,
  children,
  header,
  drawerDescription,
  ariaLabel,
  wrapperClassName,
  width,
  side,
  align,
  openOn,
  className,
  showArrow,
  openDelayMs,
  maxContentHeight,
  onOpenChange,
}: RichPopoverProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [mobileOpen, setMobileOpenState] = React.useState(false);
  const setMobileOpen = React.useCallback((open: boolean) => { setMobileOpenState(open); onOpenChange?.(open); }, [onOpenChange]);
  // Unmounting with the drawer open closes it: report it, as the desktop Popover does.
  const closeOnUnmount = React.useEffectEvent(() => { if (mobileOpen) onOpenChange?.(false); });
  React.useEffect(() => () => closeOnUnmount(), []);
  // Crossing the breakpoint swaps the popover for the drawer (or back) and unmounts the open one without its close: report it.
  const wasDesktop = React.useRef(isDesktop);
  React.useEffect(() => {
    if (wasDesktop.current === isDesktop) return;
    wasDesktop.current = isDesktop;
    setMobileOpenState(false);
    onOpenChange?.(false);
  }, [isDesktop, onOpenChange]);

  if (isDesktop) {
    return (
      <Popover
        trigger={trigger}
        ariaLabel={ariaLabel}
        wrapperClassName={wrapperClassName}
        width={width}
        side={side}
        align={align}
        openOn={openOn}
        className={className}
        showArrow={showArrow}
        openDelayMs={openDelayMs}
        maxContentHeight={maxContentHeight}
        onOpenChange={onOpenChange}
      >
        {children}
      </Popover>
    );
  }

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={mobileOpen}
        aria-label={ariaLabel}
        // Tap feedback is inherited by EVERY mobile popover trigger here (weather,
        // lineup, game-lines, etc.) rather than re-declared per trigger. Merged so
        // a custom wrapperClassName (e.g. an expanded hit area) keeps the press scale.
        className={cn(
          'transition-transform duration-150 active:scale-95 motion-reduce:active:scale-100',
          wrapperClassName ?? 'inline-flex',
        )}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setMobileOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setMobileOpen(true);
          }
        }}
      >
        {trigger}
      </span>

      <Drawer.Root open={mobileOpen} onOpenChange={setMobileOpen} dismissible>
        <Drawer.Portal>
          <Drawer.Overlay
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMobileOpen(false);
            }}
          />
          <Drawer.Content
            className={cn(
              'fixed bottom-0 left-0 right-0 z-50',
              'bg-white dark:bg-bg-primary',
              'rounded-t-2xl shadow-2xl',
              'max-h-[85%] flex flex-col outline-none',
            )}
          >
            <Drawer.Handle className="mx-auto mt-2 mb-1 h-1 w-12 rounded-full bg-gray-300 dark:bg-border-primary flex-shrink-0" />
            <Drawer.Title className="sr-only">{ariaLabel ?? 'Details'}</Drawer.Title>
            {drawerDescription && <Drawer.Description className="sr-only">{drawerDescription}</Drawer.Description>}

            <div className="flex items-center justify-between gap-2 px-4 pt-1 pb-2 border-b border-gray-200 dark:border-border-primary">
              <div className="min-w-0 flex-1">{header}</div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Close"
                className="flex-shrink-0 p-2 -m-2 rounded-lg hover:bg-muted/50 dark:hover:bg-bg-tertiary transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3">
              {children}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </>
  );
}
